import { ConfigProvider } from "antd";
import React, { useCallback, useContext, useEffect, useMemo } from "react";

import { replaceUrlWithoutNavigation, useLocalStorageState } from "../../utils/utils";
import type { CurrentLanguageSourceType } from "./imports";
import { displayLanguageToInternalCodeMap, languageCodeToAntDCodeMap, languageSources } from "./imports";

type LanguageContextState = {
  languageSetting: any;
  setChosenLanguage: (language: string) => void;
  currentLanguageData: CurrentLanguageSourceType;
  setLanguageSettingDirect: (language: string) => void;
};

const LanguageContext = React.createContext<LanguageContextState | null>(null);

export const LanguageContextProvider = (props: any) => {
  const [languageSetting, setLanguageSetting] = useLocalStorageState("languageSetting", "en");
  // Defaults to english
  const currentLanguageData: CurrentLanguageSourceType = useMemo(
    () => languageSources[languageSetting ?? "en"],
    [languageSetting],
  );

  useEffect(() => {
    const urlLanguageCode = window.location.pathname.split("/")[1];
    const sourceIndex = Object.keys(languageSources).findIndex((key) => key === urlLanguageCode);
    if (sourceIndex !== -1 && urlLanguageCode !== languageSetting) {
      setLanguageSetting(urlLanguageCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setChosenLanguage = useCallback(
    (displayLanguage: string) => {
      // Have to use window since this context doesn't lie in a <Router> (and thus can't use useLocation)
      const newSetting = displayLanguageToInternalCodeMap[displayLanguage];
      const newPath =
        window.location.pathname.toString().replace(`/${languageSetting}/`, `/${newSetting}/`) + window.location.search;
      replaceUrlWithoutNavigation(newPath);
      setLanguageSetting(newSetting);
    },
    [languageSetting, setLanguageSetting],
  );

  return (
    <LanguageContext.Provider
      value={{
        languageSetting,
        setChosenLanguage,
        currentLanguageData,
        setLanguageSettingDirect: setLanguageSetting,
      }}
    >
      <ConfigProvider locale={languageCodeToAntDCodeMap[languageSetting]}>{props.children}</ConfigProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextState => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("Language Context not available");
  }
  return ctx;
};
